/**
 * Post Depreciation Page
 * Monthly depreciation posting to general ledger
 */

import React, { useState, useEffect } from 'react'
import { Calendar, TrendingDown, AlertCircle, CheckCircle, FileText, Download } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { AdminUser } from '@/lib/authService'
import { formatCurrency } from '@/lib/currency'
import DepreciationService, { DepreciationEntry, DepreciationPostingResult } from '@/services/depreciationService'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'

interface PostDepreciationProps {
  user: AdminUser | null
}

export default function PostDepreciation({ user }: PostDepreciationProps) {
  const [loading, setLoading] = useState(false)
  const [previewEntries, setPreviewEntries] = useState<DepreciationEntry[]>([])
  const [selectedDate, setSelectedDate] = useState('')
  const [postingResult, setPostingResult] = useState<DepreciationPostingResult | null>(null)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [posting, setPosting] = useState(false)

  useEffect(() => {
    // Default to current month
    const now = new Date()
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
    setSelectedDate(firstDayOfMonth.toISOString().split('T')[0])
  }, [])

  async function handlePreview() {
    if (!user?.churchId || !selectedDate) return

    setLoading(true)
    setPreviewEntries([])
    setPostingResult(null)

    try {
      const periodDate = new Date(selectedDate)
      const entries = await DepreciationService.previewDepreciation(user.churchId, periodDate)
      setPreviewEntries(entries)
    } catch (error) {
      console.error('Error previewing depreciation:', error)
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to preview depreciation'}`)
    } finally {
      setLoading(false)
    }
  }

  function handlePostDepreciation() {
    if (previewEntries.length === 0) return
    setShowConfirmDialog(true)
  }

  async function confirmPost() {
    if (!user?.churchId || !user.id || !selectedDate) return

    setPosting(true)
    setShowConfirmDialog(false)

    try {
      const periodDate = new Date(selectedDate)
      const result = await DepreciationService.postDepreciationToLedger(
        user.churchId,
        periodDate,
        user.id,
        previewEntries
      )
      setPostingResult(result)

      if (result.success) {
        alert('Success: Depreciation posted to general ledger successfully!')
        // Refresh preview
        setPreviewEntries([])
      } else if (result.errors.length > 0) {
        alert(`Warning: ${result.errors.length} assets had errors. Check the results below.`)
      }
    } catch (error) {
      console.error('Error posting depreciation:', error)
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to post depreciation'}`)
    } finally {
      setPosting(false)
    }
  }

  const totalDepreciation = previewEntries.reduce((sum, e) => sum + e.depreciation_amount, 0)

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-background px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Post Monthly Depreciation</h1>
            <p className="text-sm text-muted-foreground">
              Calculate and post depreciation entries to general ledger
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto p-6">
        {/* Period Selection */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Select Period</CardTitle>
            <CardDescription>Choose the month for depreciation posting</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-4">
              <div className="flex-1 max-w-xs space-y-2">
                <Label htmlFor="period_date">Depreciation Period</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="period_date"
                    type="month"
                    value={selectedDate ? selectedDate.substring(0, 7) : ''}
                    onChange={(e) => {
                      const [year, month] = e.target.value.split('-')
                      const firstDay = new Date(parseInt(year), parseInt(month) - 1, 1)
                      setSelectedDate(firstDay.toISOString().split('T')[0])
                    }}
                    className="pl-10"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Depreciation will be calculated for the first day of the selected month
                </p>
              </div>

              <Button onClick={handlePreview} disabled={loading || !selectedDate}>
                {loading ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                    Loading...
                  </>
                ) : (
                  <>
                    <FileText className="mr-2 h-4 w-4" />
                    Preview Entries
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Success Result */}
        {postingResult && (
          <Alert className={postingResult.success ? 'border-green-500 bg-green-50' : 'border-yellow-500 bg-yellow-50'}>
            {postingResult.success ? (
              <CheckCircle className="h-4 w-4 text-green-600" />
            ) : (
              <AlertCircle className="h-4 w-4 text-yellow-600" />
            )}
            <AlertTitle>
              {postingResult.success ? 'Depreciation Posted Successfully' : 'Posted with Errors'}
            </AlertTitle>
            <AlertDescription>
              <div className="mt-2 space-y-1 text-sm">
                <div>Assets processed: {postingResult.assets_processed}</div>
                <div>Total depreciation: {formatCurrency(postingResult.total_depreciation, 'GHS')}</div>
                {postingResult.journal_entry_id && (
                  <div>Journal Entry ID: {postingResult.journal_entry_id}</div>
                )}
                {postingResult.errors.length > 0 && (
                  <div className="mt-2">
                    <div className="font-semibold">Errors:</div>
                    <ul className="list-disc list-inside">
                      {postingResult.errors.map((err, i) => (
                        <li key={i}>
                          {err.asset_name}: {err.error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Preview Entries */}
        {previewEntries.length > 0 && (
          <>
            {/* Summary Cards */}
            <div className="mb-6 grid gap-4 md:grid-cols-3">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Assets to Depreciate</CardTitle>
                  <TrendingDown className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{previewEntries.length}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Active assets requiring depreciation
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Depreciation</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {formatCurrency(totalDepreciation, 'GHS')}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Expense to be posted
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Journal Entry</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-sm">
                    <div>Dr. Depreciation Expense</div>
                    <div>Cr. Accumulated Depreciation</div>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Double-entry posting
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Entries Table */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Depreciation Entries Preview</CardTitle>
                    <CardDescription>
                      {previewEntries.length} entries for{' '}
                      {new Date(selectedDate).toLocaleDateString('en-US', {
                        month: 'long',
                        year: 'numeric',
                      })}
                    </CardDescription>
                  </div>
                  <Button onClick={handlePostDepreciation} disabled={posting}>
                    {posting ? (
                      <>
                        <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                        Posting...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2 h-4 w-4" />
                        Post to Ledger
                      </>
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Asset Code</TableHead>
                      <TableHead>Asset Name</TableHead>
                      <TableHead className="text-right">Book Value Before</TableHead>
                      <TableHead className="text-right">Depreciation</TableHead>
                      <TableHead className="text-right">Accum. Depreciation</TableHead>
                      <TableHead className="text-right">Book Value After</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewEntries.map((entry) => (
                      <TableRow key={entry.asset_id}>
                        <TableCell className="font-medium">{entry.asset_code}</TableCell>
                        <TableCell>{entry.asset_name}</TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(
                            entry.accumulated_depreciation_before === 0
                              ? entry.book_value_after + entry.depreciation_amount
                              : entry.book_value_after + entry.depreciation_amount,
                            'GHS'
                          )}
                        </TableCell>
                        <TableCell className="text-right font-medium text-red-600">
                          {formatCurrency(entry.depreciation_amount, 'GHS')}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatCurrency(entry.accumulated_depreciation_after, 'GHS')}
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(entry.book_value_after, 'GHS')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Journal Entry Preview */}
                <div className="mt-6 border-t pt-4">
                  <h4 className="font-semibold mb-3">Journal Entry to be Posted:</h4>
                  <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
                    <div className="grid grid-cols-3 gap-4 font-semibold border-b pb-2 mb-2">
                      <div>Account</div>
                      <div className="text-right">Debit</div>
                      <div className="text-right">Credit</div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 py-1">
                      <div>Depreciation Expense</div>
                      <div className="text-right">{formatCurrency(totalDepreciation, 'GHS')}</div>
                      <div className="text-right">-</div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 py-1">
                      <div className="pl-4">Accumulated Depreciation</div>
                      <div className="text-right">-</div>
                      <div className="text-right">{formatCurrency(totalDepreciation, 'GHS')}</div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 font-semibold border-t pt-2 mt-2">
                      <div>Total</div>
                      <div className="text-right">{formatCurrency(totalDepreciation, 'GHS')}</div>
                      <div className="text-right">{formatCurrency(totalDepreciation, 'GHS')}</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* No Entries */}
        {!loading && previewEntries.length === 0 && selectedDate && !postingResult && (
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>No Assets to Depreciate</AlertTitle>
            <AlertDescription>
              No active assets found that require depreciation for the selected period.
              <br />
              <br />
              This could mean:
              <ul className="list-disc list-inside mt-2">
                <li>All assets have already been deprecated for this period</li>
                <li>No assets have depreciation settings configured</li>
                <li>Assets have not reached their depreciation start date</li>
              </ul>
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Depreciation Posting</DialogTitle>
            <DialogDescription>
              This will create a journal entry and update asset values. This action cannot be undone easily.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Period:</span>
              <span className="font-medium">
                {new Date(selectedDate).toLocaleDateString('en-US', {
                  month: 'long',
                  year: 'numeric',
                })}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Assets:</span>
              <span className="font-medium">{previewEntries.length}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total Depreciation:</span>
              <span className="font-medium">{formatCurrency(totalDepreciation, 'GHS')}</span>
            </div>
          </div>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              The journal entry will be posted immediately and cannot be deleted. You would need to create a
              reversing entry if corrections are needed.
            </AlertDescription>
          </Alert>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button onClick={confirmPost}>Confirm and Post</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
